import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, TrendingUp, TrendingDown, Activity, Radio, RefreshCw, ChevronDown, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { downloadCsv, csvButtonBase } from "@/lib/csv";

const SECTIONS = [
  { key: "golden_ma",       label: "EMA 20/50 Altın Kesişim",   icon: TrendingUp,   tone: "up" },
  { key: "golden_macd",     label: "MACD Altın Kesişim",        icon: TrendingUp,   tone: "up" },
  { key: "bb_upper_break",  label: "Bollinger Üst Kırılım",     icon: Activity,     tone: "up" },
  { key: "vol_spikes",      label: "Hacim Patlaması",           icon: Radio,        tone: "up" },
  { key: "death_ma",        label: "EMA 20/50 Ölüm Kesişimi",   icon: TrendingDown, tone: "down" },
  { key: "death_macd",      label: "MACD Ölüm Kesişimi",        icon: TrendingDown, tone: "down" },
  { key: "bb_lower_break",  label: "Bollinger Alt Kırılım",     icon: Activity,     tone: "down" },
];

export default function DailySummaryPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null); // null = latest
  const [showDatePicker, setShowDatePicker] = useState(false);
  const nav = useNavigate();

  const load = async (date) => {
    setLoading(true);
    try {
      const q = date ? `?date=${date}` : "";
      const { data } = await api.get(`/daily-summary${q}`);
      setData(data);
    } catch (e) {
      if (e.response?.status === 404) toast.error("Seçilen gün için özet yok");
      else toast.error("Özet yüklenemedi");
    } finally { setLoading(false); }
  };
  const loadDates = async () => {
    try { const { data } = await api.get("/daily-summary/dates"); setDates(data.dates || []); } catch {}
  };

  useEffect(() => { load(null); loadDates(); }, []);

  const pickDate = (d) => {
    setSelectedDate(d);
    setShowDatePicker(false);
    load(d);
  };
  const backToLatest = () => { setSelectedDate(null); setShowDatePicker(false); load(null); };

  if (loading) return <div className="p-16 flex justify-center"><div className="radar-loader" /></div>;
  if (!data) return null;

  const totalCount = SECTIONS.reduce((sum, s) => sum + (data[s.key]?.length || 0), 0);

  return (
    <div className="space-y-6" data-testid="daily-summary-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary ztan-title flex items-center gap-2">
            <CalendarDays size={22} className="text-brand-blue" /> Günün Özeti
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            {data.date} · {totalCount} sinyal · Her seans 18:20 Europe/Istanbul'da güncellenir
            {data.computed_on_demand && (
              <span className="ml-2 text-[10px] px-2 py-0.5 rounded border text-accent-yellow bg-accent-yellow/10 border-accent-yellow/30">
                Anlık hesaplama (henüz snapshot yok)
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button
              data-testid="daily-summary-date-picker"
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="bg-bg-panel border border-bg-border text-text-primary px-3 py-1.5 rounded-lg text-xs hover:bg-bg-hover flex items-center gap-1.5"
            >
              <CalendarDays size={12} /> {selectedDate || data.date} <ChevronDown size={11} />
            </button>
            {showDatePicker && (
              <div className="absolute right-0 mt-1 w-52 bg-bg-panel border border-bg-border rounded-lg shadow-2xl overflow-hidden z-30 max-h-72 overflow-y-auto">
                <button
                  data-testid="daily-date-latest"
                  onClick={backToLatest}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-bg-hover ${!selectedDate ? "text-brand-blue" : "text-text-primary"}`}
                >En Güncel</button>
                {dates.length === 0 ? (
                  <div className="px-3 py-3 text-[11px] text-text-secondary italic">Henüz arşiv yok</div>
                ) : dates.map((d) => (
                  <button
                    key={d}
                    data-testid={`daily-date-${d}`}
                    onClick={() => pickDate(d)}
                    className={`w-full text-left px-3 py-1.5 text-xs font-mono border-t border-bg-border hover:bg-bg-hover ${selectedDate === d ? "text-brand-blue" : "text-text-primary"}`}
                  >{d}</button>
                ))}
              </div>
            )}
          </div>
          <button
            data-testid="daily-summary-csv"
            onClick={() => {
              const rows = [];
              SECTIONS.forEach((s) => {
                (data[s.key] || []).forEach((it) => rows.push({
                  section: s.label, symbol: it.symbol, name: it.name,
                  close: it.close, pct_change: it.pct_change, volume_ratio: it.volume_ratio,
                }));
              });
              if (!rows.length) { toast.info("İçerik yok"); return; }
              downloadCsv(`ztan_gunun_ozeti_${data.date}.csv`, rows, ["section","symbol","name","close","pct_change","volume_ratio"]);
            }}
            className={csvButtonBase()}
          ><Download size={12} /> CSV</button>
          <button
            data-testid="daily-summary-refresh"
            onClick={() => load(selectedDate)}
            className="bg-bg-hover text-text-primary border border-bg-border px-3 py-1.5 rounded-lg text-xs hover:bg-bg-border flex items-center gap-1.5"
          ><RefreshCw size={12} /> Yenile</button>
        </div>
      </div>

      {totalCount === 0 ? (
        <div className="bg-bg-panel border border-bg-border rounded-xl p-16 text-center">
          <CalendarDays size={28} className="text-text-secondary mx-auto mb-3" />
          <h3 className="text-text-primary font-semibold mb-1">Bugün için sinyal yok</h3>
          <p className="text-sm text-text-secondary">Piyasa henüz kesişim veya hacim patlaması üretmemiş.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {SECTIONS.filter((s) => (data[s.key]?.length || 0) > 0).map((s) => (
            <SectionCard
              key={s.key}
              section={s}
              items={data[s.key]}
              onSymbolClick={(sym) => nav(`/stock/${sym}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SectionCard({ section, items, onSymbolClick }) {
  const Icon = section.icon;
  const toneCls = section.tone === "up" ? "text-signal-up bg-signal-up/10 border-signal-up/30" : "text-signal-down bg-signal-down/10 border-signal-down/30";
  return (
    <div data-testid={`daily-section-${section.key}`} className="bg-bg-panel border border-bg-border rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-bg-border flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${toneCls}`}>
            <Icon size={14} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">{section.label}</h3>
            <p className="text-[11px] text-text-secondary">{items.length} hisse</p>
          </div>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {items.map((it) => (
          <button
            key={it.symbol}
            data-testid={`daily-item-${it.symbol}-${section.key}`}
            onClick={() => onSymbolClick(it.symbol)}
            className="w-full flex items-center justify-between px-4 py-2.5 border-b border-bg-border hover:bg-bg-hover transition-colors last:border-b-0"
          >
            <div className="text-left min-w-0">
              <div className="font-mono font-bold text-text-primary text-sm">{it.symbol}</div>
              <div className="text-[10px] text-text-secondary truncate">{it.name}</div>
            </div>
            <div className="text-right flex-shrink-0">
              {it.close != null && <div className="text-xs font-mono text-text-primary">₺{fmt(it.close)}</div>}
              <div className={`text-xs font-mono ${(it.pct_change ?? 0) >= 0 ? "text-signal-up" : "text-signal-down"}`}>
                {it.pct_change != null ? `${it.pct_change > 0 ? "+" : ""}${it.pct_change.toFixed(2)}%` : "-"}
              </div>
              {it.volume_ratio && <div className="text-[10px] text-text-secondary">{it.volume_ratio.toFixed(1)}x hacim</div>}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function fmt(v, d = 2) {
  if (v == null) return "-";
  return Number(v).toLocaleString("tr-TR", { minimumFractionDigits: d, maximumFractionDigits: d });
}
